import { DiffEditor, type DiffOnMount } from "@monaco-editor/react";
import { AlertTriangle, XCircle } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/components/ui";

interface DefinitionDiffModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  rawJson: Record<string, unknown>;
  workingJson: Record<string, unknown>;
  zodSchema: z.ZodType;
  knownKeys: ReadonlySet<string>;
}

export const DefinitionDiffModal: React.FC<DefinitionDiffModalProps> = ({
  open,
  onClose,
  onConfirm,
  rawJson,
  workingJson,
  zodSchema,
  knownKeys,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<Parameters<DiffOnMount>[0] | null>(null);

  const handleEditorMount: DiffOnMount = useCallback((editor) => {
    editorRef.current = editor;
  }, []);

  // Dispose the DiffEditor before the Dialog exit animation starts.
  // Monaco's DiffEditorWidget throws "TextModel got disposed before
  // DiffEditorWidget model got reset" when the Dialog unmounts children
  // during its closing transition, because models are torn down before the
  // widget resets its internal references.  Eagerly disposing on `open →
  // false` ensures the widget cleans up in the correct order.
  useEffect(() => {
    if (!open && editorRef.current) {
      editorRef.current.dispose();
      editorRef.current = null;
    }
  }, [open]);

  // Only compute expensive operations when the modal is open
  const validationResult = useMemo(
    () => (open ? zodSchema.safeParse(workingJson) : { success: true as const, data: workingJson }),
    [open, zodSchema, workingJson],
  );

  const unknownFields = useMemo(
    () => (open ? Object.keys(workingJson).filter((k) => !knownKeys.has(k)) : []),
    [open, workingJson, knownKeys],
  );

  const originalJson = useMemo(() => (open ? JSON.stringify(rawJson, null, 2) : ""), [open, rawJson]);
  const modifiedJson = useMemo(() => (open ? JSON.stringify(workingJson, null, 2) : ""), [open, workingJson]);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[92vh] max-w-[min(1100px,92vw)] flex-col">
        <DialogHeader>
          <DialogTitle>{t("ui.definitionDiff.title")}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
          {!validationResult.success ? (
            <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-score-c bg-score-c-bg p-3 text-14-regular text-score-c">
              <XCircle className="mt-0.5 size-4 shrink-0" />
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-14-bold">{t("ui.definitionDiff.schemaError")}</p>
                {validationResult.error.issues.map((issue, i) => (
                  <p key={i}>
                    {issue.path.length > 0 ? `${issue.path.join(".")}: ` : ""}
                    {issue.message}
                  </p>
                ))}
              </div>
            </div>
          ) : null}

          {unknownFields.length > 0 ? (
            <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-score-b bg-score-b-bg p-3 text-14-regular text-[#B25612]">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <p className="min-w-0 flex-1">
                {t("ui.definitionDiff.unknownFields", { fields: unknownFields.join(", ") })}
              </p>
            </div>
          ) : null}

          <div className="flex gap-1 text-12-regular text-fg-3">
            <span className="flex-1">{t("ui.definitionDiff.original")}</span>
            <span className="flex-1">{t("ui.definitionDiff.modified")}</span>
          </div>
          <div className="overflow-hidden rounded-[var(--radius-md)] border border-border-default">
            <DiffEditor
              height="60vh"
              language="json"
              original={originalJson}
              modified={modifiedJson}
              onMount={handleEditorMount}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                lineNumbers: "on",
                scrollBeyondLastLine: false,
                automaticLayout: true,
                renderSideBySide: true,
              }}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("ui.cancel")}
          </Button>
          <Button type="button" onClick={onConfirm}>
            {t("ui.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
